import { GeckoColorScaleThreshold } from 'types/measurement/geckoColorScaleThreshold'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { parseString } from 'utils/parsingHelpers'

import { GeckoEntryBase } from './geckoEntry'
import { RolodexEntryJson } from './rolodexEntryFields'

/**
 * An entry containing a set of "defaults" for how a component should be
 * displayed, including color scale thresholds, etc.
 */
export class GeckoComponentDefaults extends GeckoEntryBase<
  {
    color_scale_thresholds: ReadonlyMap<string, GeckoColorScaleThreshold>
  },
  {
    organization: string
    site: string
    unit: string
    component: string
  }
> {
  private constructor(json: RolodexEntryJson) {
    // Extract all the color_scale_thresholds per view
    const color_scale_thresholds = new Map<string, GeckoColorScaleThreshold>()
    const color_scale_thresholdsJSON =
      (json.data.color_scale_thresholds as
        | Record<string, Record<string, number>>
        | undefined) ?? {}

    Object.entries(color_scale_thresholdsJSON).forEach(([key, value]) => {
      if (!value) return
      if (color_scale_thresholds.has(key))
        throw new Error(`Duplicate view "${key}" in defaults ${json.id}`)
      try {
        const min: number = value['min']
        const max: number = value['max']
        const mid: number = value['mid'] ?? (min + max) / 2.0
        const measurement_unit = GeckoMeasurementUnit.GetOrCreate(
          parseString(value['measurement_unit']) ?? ''
        )
        const threshold = GeckoColorScaleThreshold.CreateFromValues(
          min,
          mid,
          max,
          measurement_unit
        )

        if (threshold) {
          color_scale_thresholds.set(key, threshold)
        }
      } catch (error) {
        console.error(`Markup layer "${json.id} had error "`, error)
      }
    })

    super(
      json,
      { color_scale_thresholds },
      {
        organization: parseString(json.tags.organization) ?? '',
        site: parseString(json.tags.site) ?? '',
        unit: parseString(json.tags.unit) ?? '',
        component: parseString(json.tags.component) ?? '',
      },
      {}
    )
  }

  /**
   * A simple factory method which creates componentDefaults from raw JSON
   * @param json The raw JSON to create an instance from
   * @constructor
   */
  public static CreateFromJSON(json: RolodexEntryJson) {
    return new GeckoComponentDefaults(json)
  }
}
